import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { BarChart3, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function AdminMarketing() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/site-config").then(({ data }) => setCfg(data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/site-config", {
        ga4_id: cfg.ga4_id, meta_pixel_id: cfg.meta_pixel_id,
        google_ads_id: cfg.google_ads_id, google_ads_purchase_label: cfg.google_ads_purchase_label,
        play_store_url: cfg.play_store_url, app_store_url: cfg.app_store_url,
        app_promo_enabled: cfg.app_promo_enabled,
      });
      setCfg(data);
      toast.success("Marketing settings saved.");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="p-10 text-zinc-500">Loading…</div>;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  return (
    <div className="p-6 md:p-10 space-y-8 max-w-3xl" data-testid="admin-marketing-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Growth</p>
        <h1 className="font-display text-3xl text-white uppercase tracking-tight">Marketing & Analytics</h1>
      </header>

      <section className="border border-[#27272A] bg-[#0F0F0F] p-6 space-y-5">
        <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#FF331F]" /><h2 className="font-display uppercase tracking-widest text-sm">Conversion Tracking</h2></div>
        <p className="text-xs text-zinc-500">Paste your IDs and the site wires them automatically — no redeploy of code needed (just refresh).</p>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Google Analytics 4 (Measurement ID)</Label>
          <Input value={cfg.ga4_id || ""} onChange={(e) => set("ga4_id", e.target.value)} placeholder="G-XXXXXXXXXX"
            data-testid="ga4-input" className="mt-1 bg-black border-[#27272A] text-white font-mono" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Meta (Facebook) Pixel ID</Label>
          <Input value={cfg.meta_pixel_id || ""} onChange={(e) => set("meta_pixel_id", e.target.value)} placeholder="123456789012345"
            data-testid="meta-input" className="mt-1 bg-black border-[#27272A] text-white font-mono" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Google Ads Conversion ID</Label>
          <Input value={cfg.google_ads_id || ""} onChange={(e) => set("google_ads_id", e.target.value)} placeholder="AW-XXXXXXXXX"
            data-testid="google-ads-id-input" className="mt-1 bg-black border-[#27272A] text-white font-mono" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Google Ads Purchase Conversion Label</Label>
          <Input value={cfg.google_ads_purchase_label || ""} onChange={(e) => set("google_ads_purchase_label", e.target.value)} placeholder="AbC-D_efG"
            data-testid="google-ads-label-input" className="mt-1 bg-black border-[#27272A] text-white font-mono" />
          <p className="text-[11px] text-zinc-600 mt-1">Fires on every completed purchase (bookings, coaching, library, meetups). The ID + label come from your Google Ads conversion snippet.</p>
        </div>
      </section>

      <section className="border border-[#27272A] bg-[#0F0F0F] p-6 space-y-5">
        <div className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-[#FF331F]" /><h2 className="font-display uppercase tracking-widest text-sm">App Promotion</h2></div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white text-sm font-semibold">Show &quot;Get the app&quot; section</div>
            <div className="text-xs text-zinc-500">Displays a Google Play badge + QR on the homepage and blog posts.</div>
          </div>
          <Switch checked={!!cfg.app_promo_enabled} onCheckedChange={(v) => set("app_promo_enabled", v)} data-testid="app-promo-toggle" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Google Play URL</Label>
          <Input value={cfg.play_store_url || ""} onChange={(e) => set("play_store_url", e.target.value)}
            data-testid="play-url-input" className="mt-1 bg-black border-[#27272A] text-white font-mono text-xs" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">App Store URL (optional — when iOS launches)</Label>
          <Input value={cfg.app_store_url || ""} onChange={(e) => set("app_store_url", e.target.value)} placeholder="https://apps.apple.com/…"
            data-testid="appstore-url-input" className="mt-1 bg-black border-[#27272A] text-white font-mono text-xs" />
        </div>
      </section>

      <Button onClick={save} disabled={saving} data-testid="marketing-save"
        className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest text-xs font-bold">
        <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
}
